import Iconify from "@/components/ui/Iconify";
import { Collapse } from "@mui/material";
import { useTranslations } from "@/lib/static-intl";
import { useRef, useState } from "react";
import { placeFilters as defaultFilters } from "@/constants";
import useScrollOnDrag from "react-scroll-ondrag";
import { useSearchParams } from "next/navigation";

const PlacesListFilters = ({
  placeFilters,
  setPlaceFilters,
  handlePlaceSorting,
}) => {
  const t = useTranslations("places.place_search_filters");
  const searchParams = useSearchParams();
  const searchPlace = searchParams.get("search-place");

  const selectedFilterRef = useRef();
  const { events: scrollEvents } = useScrollOnDrag(selectedFilterRef);

  const selectedSortRef = useRef();
  const { events: scrollSortEvents } = useScrollOnDrag(selectedSortRef);

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [activePlaceFilterName, setActivePlaceFilterName] = useState(
    defaultFilters?.filter((f) => f.show === true)?.[0]?.filter_name || ""
  );

  const [currentSort, setCurrentSort] = useState(
    searchPlace ? "relevance" : "distance"
  );
  const [filters, setFilters] = useState(
    defaultFilters?.filter((f) => f.show === true)
  );

  const handleOptionChange = (filterName, optionValue, isMultiple) => {
    const updatedFilters = filters.map((filter) => {
      if (filter.filter_name !== filterName) return filter;

      const updatedOptions = filter.filter_options.map((option) => {
        if (isMultiple) {
          return option.filter_value === optionValue
            ? { ...option, is_selected: !option.is_selected }
            : option;
        } else {
          return {
            ...option,
            is_selected: option.filter_value === optionValue,
          };
        }
      });

      return { ...filter, filter_options: updatedOptions };
    });

    setFilters(updatedFilters);

    // Extract selected options
    const selectedOptions =
      updatedFilters
        .find((f) => f.filter_name === filterName)
        ?.filter_options?.filter((o) => o.is_selected) || [];

    // Immediate set placeFilters
    setPlaceFilters((prev) => {
      const updated = { ...prev };

      if (selectedOptions.length > 0) {
        updated[filterName] = selectedOptions;
      } else {
        delete updated[filterName];
      }

      return updated;
    });
  };

  const handleClearAllFilters = () => {
    setFilters(defaultFilters.filter((f) => f.show === true));
    setPlaceFilters(null);
  };

  const handleSortingClick = (sort) => {
    if (sort) {
      setCurrentSort(sort);
      handlePlaceSorting(sort);
    }
  };

  const handleRemoveFilter = (filterLabel) => {
    // Step 1: Update filters state to deselect the option
    setFilters((prevFilters) =>
      prevFilters.map((filter) => {
        const updatedOptions = filter.filter_options.map((option) => {
          if (option.filter_label === filterLabel) {
            return {
              ...option,
              is_selected: false,
            };
          }
          return option;
        });

        return {
          ...filter,
          filter_options: updatedOptions,
        };
      })
    );

    // Step 2: Update placeFilters state to remove it
    setPlaceFilters((prev) => {
      const updated = { ...prev };

      for (const key in updated) {
        const filtered = updated[key].filter(
          (opt) => opt.filter_label !== filterLabel
        );
        if (filtered.length > 0) {
          updated[key] = filtered;
        } else {
          delete updated[key];
        }
      }

      return updated;
    });
  };

  const activePlaceFilter = filters.find(
    (f) => f.filter_name === activePlaceFilterName
  );

  return (
    // 배경을 분홍으로 칠하지 않는다. 핑크는 "저장·신청" 같은 행동에만 쓰고,
    // 필터 줄은 지금 상태를 알려주는 자리다(디자인 규칙 §10).
    <div className="w-full flex flex-col px-2 py-2 border-b border-black/5 dark:border-white/10">
      <Collapse in={isFiltersExpanded} timeout={500}>
        <div className="w-full flex flex-col gap-2">
          <div className="w-full flex max-h-[270px] overflow-hidden">
            {/* FILTER TABS */}
            <div className="min-w-[130px] flex flex-col gap-2">
              {filters.map((filter, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActivePlaceFilterName(filter?.filter_name)}
                  className={`flex items-center border dark:border-transparent ${
                    activePlaceFilterName === filter?.filter_name
                      ? "bg-white dark:bg-heading_black"
                      : "bg-gray-100 dark:bg-light_primary_label"
                  } rounded-l-full rounded-r overflow-hidden pr-1 transition ease-in-out duration-200`}
                >
                  <div
                    className="w-5 h-full"
                    style={{
                      backgroundColor:
                        activePlaceFilterName === filter?.filter_name
                          ? filter?.filter_color
                          : "transparent",
                    }}
                  />
                  <span className="text-xs my-1 pl-1 text-start">
                    {t(filter?.filter_name)}
                  </span>
                </button>
              ))}
            </div>

            {/* FILTER OPTIONS */}
            <div
              key={activePlaceFilterName}
              className="w-full h-fit max-h-full overflow-y-auto overflow-x-hidden thin-scrollbar grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-2 items-start p-3"
            >
              {activePlaceFilter?.filter_options?.map((option, i) => (
                <label
                  key={i}
                  className="w-full flex items-start gap-1.5 cursor-pointer"
                >
                  <input
                    type={
                      activePlaceFilter?.can_choose_multiple
                        ? "checkbox"
                        : "radio"
                    }
                    name={activePlaceFilter?.filter_name}
                    value={option?.filter_value}
                    checked={option?.is_selected}
                    onChange={() =>
                      handleOptionChange(
                        activePlaceFilter?.filter_name,
                        option?.filter_value,
                        activePlaceFilter?.can_choose_multiple
                      )
                    }
                  />
                  <span className="text-xs">{t(option?.filter_label)}</span>
                </label>
              ))}
            </div>
          </div>
          <div
            ref={selectedSortRef}
            {...scrollSortEvents}
            className="w-full flex items-center justify-start gap-4 px-1 py-2 overflow-x-auto hide-scrollbar"
          >
            {(searchPlace
              ? placeSortEvents
              : placeSortEvents?.filter(
                  (_p) => _p?.label !== "sort_by_relevance"
                )
            )?.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleSortingClick(item?.sorting)}
                className={`text-nowrap border text-sm rounded-md px-2 py-1 ${
                  currentSort === item?.sorting
                    ? "border-red text-red dark:border-white dark:text-white font-medium"
                    : "text-black/50 dark:text-white/60 dark:border-white/50"
                } transition`}
              >
                {t(item?.label)}
              </button>
            ))}
          </div>
        </div>
      </Collapse>
      {/* EXPAND/COLLAPSE BUTTON */}
      <div className="w-full flex flex-col">
        {!!placeFilters &&
          Object.values(placeFilters)?.some?.((f) => f?.length > 0) &&
          Object.values(placeFilters)
            ?.flat()
            ?.filter?.((f) => f?.is_selected === true)
            ?.map((f) => f?.filter_label)?.length > 0 && (
            <div
              className={`w-full flex items-center gap-2 py-2 px-1 ${
                isFiltersExpanded ? "pt-1" : "pt-0"
              }`}
            >
              <div
                ref={selectedFilterRef}
                {...scrollEvents}
                className="w-full flex items-center gap-3 overflow-x-auto hide-scrollbar"
              >
                {Object.values(placeFilters)
                  ?.flat()
                  ?.filter?.((f) => f?.is_selected === true)
                  ?.map((f) => f?.filter_label)
                  ?.reverse()
                  ?.map((f_label, i) => (
                    <div
                      key={i}
                      className="text-sm text-nowrap py-1 pl-2.5 pr-2 rounded-md bg-rose-500 text-white flex items-center justify-center gap-1"
                    >
                      {t(f_label)}
                      <button
                        type="button"
                        onClick={() => handleRemoveFilter(f_label)}
                        className="bg-rose-800 rounded-full min-w-5 w-5 h-5 scale-75 flex items-center justify-center"
                      >
                        <Iconify
                          icon={"material-symbols:close-rounded"}
                          className={"!min-w-4 !w-4 !h-4 -ml-[1px] !text-white"}
                        />
                      </button>
                    </div>
                  ))}
              </div>
              <button type="button" onClick={handleClearAllFilters}>
                <Iconify icon={"hugeicons:undo"} />
              </button>
            </div>
          )}
        <div className="w-full flex items-center justify-end">
          <button
            type="button"
            className="rounded-full px-3 py-1 text-xs flex items-center gap-1 opacity-60 hover:opacity-100 transition ease-in-out duration-200"
            onClick={() => setIsFiltersExpanded((prev) => !prev)}
          >
            <Iconify
              icon={"mage:double-arrow-down"}
              className={`!w-3 !h-3 ${
                isFiltersExpanded ? "rotate-180" : ""
              } transition ease-in-out duration-200`}
            />
            <span>
              {isFiltersExpanded ? t("hide_filters") : t("show_filters")}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlacesListFilters;

const placeSortEvents = [
  {
    label: "sort_by_relevance",
    sorting: "relevance",
  },
  {
    label: "by_distance",
    sorting: "distance",
  },
  {
    label: "sort_by_rating",
    sorting: "rating",
  },
  {
    label: "sort_by_num_of_reviews",
    sorting: "numOfReviews",
  },
];
